'use client'

import { useState, useCallback, useEffect, useRef } from 'react'
import { RefreshCw } from 'lucide-react'
import type { ProcessedTheme, BrowserRow, BrowserDataDescription } from './processedTypes'
import { watchClasses, compactDateNumeric, timelineDateLabel, findDescription } from './processedTypes'
import { ScreenshotLightbox, ImageTile, extractScreenshotImages } from './imageComponents'
import { Widget } from '../../Widget'

function ReloadButton({ onClick, loading, theme }: { onClick: () => void; loading: boolean; theme: ProcessedTheme }) {
  const c = watchClasses(theme)
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      title="Reload images"
      aria-label="Reload images"
      className={`shrink-0 rounded p-1 transition-colors hover:text-signal disabled:opacity-50 ${c.faint}`}
    >
      <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
    </button>
  )
}

type HistoryItem = { id: string; createdAt: string; descriptions?: BrowserDataDescription[] }
type HistoryImageState = 'loading' | 'error' | string // string = loaded image data URI

// One gallery thumbnail — starts idle, and only asks its parent to actually
// fetch its image once its own DOM node scrolls into view (IntersectionObserver),
// so opening an entry with dozens of historical captures doesn't fire dozens
// of screenshot fetches (each needing its own Turnstile round-trip) up front.
function GalleryThumbnail({
  item,
  imageState,
  onVisible,
  onOpen,
  theme,
}: {
  item: HistoryItem
  imageState: HistoryImageState | undefined
  onVisible: (id: string) => void
  onOpen: (src: string, label: string, description: string | null) => void
  theme: ProcessedTheme
}) {
  const c = watchClasses(theme)
  const ref = useRef<HTMLButtonElement>(null)
  const label = compactDateNumeric(item.createdAt)
  const loaded = imageState && imageState !== 'loading' && imageState !== 'error' ? imageState : null
  const hasDescription = (item.descriptions ?? []).some((d) => d.description)

  useEffect(() => {
    const el = ref.current
    if (!el || imageState) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) onVisible(item.id) },
      { rootMargin: '150px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [item.id, imageState, onVisible])

  return (
    <button
      ref={ref}
      type="button"
      onClick={() => {
        // Lazy-load is IntersectionObserver-driven, which can just not fire
        // (a short/narrow strip, a scroll container the observer doesn't
        // pick up cleanly, …) — rather than leave a thumbnail permanently
        // stuck un-clickable when that happens, a click always either opens
        // the loaded image or (if it isn't loaded yet) forces the same load
        // onVisible would have triggered, same as scrolling it into view.
        if (loaded) {
          const desc = [findDescription(item.descriptions, 'screenshot_mobile_description'), findDescription(item.descriptions, 'screenshot_desktop_description')]
            .filter(Boolean)
            .join(' · ') || null
          onOpen(loaded, label, desc)
        } else {
          onVisible(item.id)
        }
      }}
      title={imageState === 'error' ? `${label} — failed to load, click to retry` : !loaded && imageState !== 'loading' ? `${label} — click to load` : hasDescription ? `${label} — has description` : label}
      className={`relative aspect-[4/5] w-16 shrink-0 overflow-hidden rounded border transition-colors ${c.line} ${c.soft} ${!loaded && imageState !== 'loading' ? 'hover:border-signal' : ''}`}
    >
      {loaded ? (
        <img src={loaded} alt={label} className="h-full w-full object-cover object-top" />
      ) : (
        <span className={`flex h-full w-full items-center justify-center font-mono text-[9px] ${imageState === 'error' ? 'text-destructive' : c.faint}`}>
          {imageState === 'loading' ? '···' : imageState === 'error' ? 'retry' : '↓'}
        </span>
      )}
      {hasDescription && (
        <span className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-signal" />
      )}
      <span className="absolute inset-x-0 bottom-0 truncate bg-overlay px-0.5 py-0.5 text-center font-mono text-[7px] text-white">
        {label}
      </span>
    </button>
  )
}

export function LatestObtainedImagesPanel({
  entryId,
  userEntryDomainId,
  latestBrowser,
  theme,
  getToken,
  bare = false,
  onImagesLoaded,
}: {
  entryId: string
  userEntryDomainId?: string | null
  latestBrowser: BrowserRow | null
  theme: ProcessedTheme
  getToken: () => Promise<string>
  bare?: boolean
  onImagesLoaded?: (desktop: string | null, mobile: string | null) => void
}) {
  const c = watchClasses(theme)
  const [state, setState] = useState<'loading' | 'loaded' | 'error' | 'no-data'>('loading')
  const [desktop, setDesktop] = useState<string | null>(null)
  const [mobile, setMobile] = useState<string | null>(null)
  const [historyLabel, setHistoryLabel] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<{ src: string; label: string; description?: string | null } | null>(null)
  const [galleryItems, setGalleryItems] = useState<HistoryItem[]>([])
  const [galleryImages, setGalleryImages] = useState<Record<string, HistoryImageState>>({})

  // getToken() drives a single shared Turnstile widget (one pending promise
  // at a time — see CaseEntryDetailPage's getScreenshotToken) — calling it
  // again before the previous call resolves clobbers that pending promise
  // and leaves the first caller hanging forever. The "latest" load below,
  // the gallery's history-list fetch, and every lazily-triggered thumbnail
  // fetch all need a token, so all of them are funneled through this single
  // queue instead of firing independently.
  const queueRef = useRef<Promise<unknown>>(Promise.resolve())
  const runQueued = useCallback(<T,>(task: () => Promise<T>): Promise<T> => {
    const run = queueRef.current.then(task, task)
    queueRef.current = run.then(() => undefined, () => undefined)
    return run
  }, [])

  const load = useCallback(async () => {
    setState('loading')
    setDesktop(null)
    setMobile(null)
    try {
      const result = await runQueued(async () => {
        const token = await getToken()
        const res = await fetch('/api/screenshots', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            processedEntryId: entryId,
            ...(userEntryDomainId != null && { userEntryDomainId }),
            turnstileToken: token,
          }),
        })
        return res.json()
      })
      if (!result.success) { setState('error'); return }

      const inner = result.data?.data
      const rawHistory = Array.isArray(inner?.history) ? inner.history : []
      const firstWithImage = rawHistory.find((h: any) => h.hasImage)
      setHistoryLabel(firstWithImage?.createdAt ?? inner?.capturedAt ?? null)

      const shots = extractScreenshotImages(result.data)
      if (!shots?.desktop && !shots?.mobile) { setState('no-data'); return }
      setDesktop(shots.desktop)
      setMobile(shots.mobile)
      setState('loaded')
      onImagesLoaded?.(shots.desktop, shots.mobile)
    } catch {
      setState('error')
    }
  }, [entryId, userEntryDomainId, getToken, runQueued])

  useEffect(() => { load() }, [load])

  // Gallery — the full capture history for this domain entry, lazy-loaded
  // (see GalleryThumbnail). The list itself (id + createdAt, no image bytes)
  // is cheap, so it's fetched once up front; each thumbnail's actual image
  // only loads once it scrolls into view.
  useEffect(() => {
    if (!userEntryDomainId) { setGalleryItems([]); return }
    let cancelled = false
    runQueued(async () => {
      const token = await getToken()
      const res = await fetch('/api/screenshots/history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userEntryDomainId, turnstileToken: token }),
      })
      return res.json()
    }).then((result) => {
      if (cancelled) return
      const items = result.success && Array.isArray(result.data) ? result.data : []
      setGalleryItems(items)
    }).catch(() => { if (!cancelled) setGalleryItems([]) })
    return () => { cancelled = true }
  }, [userEntryDomainId, getToken, runQueued])

  // Ref (not the galleryImages state) is what actually dedupes concurrent
  // triggers — several thumbnails can report visible in the same tick, and
  // reading state inside this closure would only ever see the value from
  // whenever it was last created, not "as of right now". Cleared again on
  // failure so a click on a failed thumbnail (or the observer re-firing if
  // it's still visible) can retry instead of being permanently stuck.
  const inFlightRef = useRef<Set<string>>(new Set())
  const loadGalleryItem = useCallback((id: string) => {
    if (inFlightRef.current.has(id)) return
    inFlightRef.current.add(id)
    setGalleryImages((prev) => ({ ...prev, [id]: 'loading' }))
    runQueued(async () => {
      const token = await getToken()
      const res = await fetch('/api/screenshots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryBrowserDataId: id, turnstileToken: token }),
      })
      return res.json()
    }).then((result) => {
      const shots = result.success ? extractScreenshotImages(result.data) : null
      const image = shots?.desktop ?? shots?.mobile ?? null
      if (!image) inFlightRef.current.delete(id)
      setGalleryImages((prev) => ({ ...prev, [id]: image ?? 'error' }))
    }).catch(() => {
      inFlightRef.current.delete(id)
      setGalleryImages((prev) => ({ ...prev, [id]: 'error' }))
    })
  }, [getToken, runQueued])

  const gallery = galleryItems.length > 0 ? (
    <div className="mt-3">
      <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${c.faint}`}>
        History · {galleryItems.length} capture{galleryItems.length === 1 ? '' : 's'}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {galleryItems.map((item) => (
          <GalleryThumbnail
            key={item.id}
            item={item}
            imageState={galleryImages[item.id]}
            onVisible={loadGalleryItem}
            onOpen={(src, label, description) => setLightbox({ src, label: `Capture · ${label}`, description })}
            theme={theme}
          />
        ))}
      </div>
    </div>
  ) : null

  const capturedAt = latestBrowser?.createdAt ?? ''
  const mobileDescription = findDescription(latestBrowser?.descriptions, 'screenshot_mobile_description')
  const desktopDescription = findDescription(latestBrowser?.descriptions, 'screenshot_desktop_description')

  const tiles = latestBrowser ? (
    <div className="grid grid-cols-2 gap-3">
      <ImageTile
        label="Mobile"
        src={mobile}
        caption={compactDateNumeric(capturedAt)}
        description={mobileDescription}
        fallback={state === 'loading' ? 'Loading' : state === 'error' ? <button onClick={load} className="text-destructive">Retry</button> : 'No image'}
        onExpand={() => mobile && setLightbox({ src: mobile, label: 'Mobile · iOS Safari', description: mobileDescription })}
      />
      <ImageTile
        label="Desktop"
        src={desktop}
        caption={compactDateNumeric(capturedAt)}
        description={desktopDescription}
        fallback={state === 'loading' ? 'Loading' : state === 'error' ? <button onClick={load} className="text-destructive">Retry</button> : 'No image'}
        onExpand={() => desktop && setLightbox({ src: desktop, label: 'Desktop · Chrome', description: desktopDescription })}
      />
    </div>
  ) : (
    <div className={`rounded-md border px-4 py-8 text-center font-mono text-[11px] uppercase tracking-[0.24em] ${c.line} ${c.soft} ${c.faint}`}>
      No browser images
    </div>
  )

  if (bare) {
    return (
      <>
        {lightbox && <ScreenshotLightbox src={lightbox.src} label={lightbox.label} description={lightbox.description} onClose={() => setLightbox(null)} />}
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className={`font-mono text-[11px] uppercase tracking-[0.28em] ${c.label}`}>// Last obtained images</span>
          <div className="flex items-center gap-2">
            {latestBrowser?.createdAt && <span className={`font-mono text-[10px] ${c.faint}`}>{compactDateNumeric(latestBrowser.createdAt)}</span>}
            <ReloadButton onClick={load} loading={state === 'loading'} theme={theme} />
          </div>
        </div>
        {tiles}
        {gallery}
      </>
    )
  }

  return (
    <Widget
      label="Last obtained images"
      theme={theme}
      headerRight={
        <div className="flex items-center justify-end gap-2">
          {latestBrowser?.createdAt && <span className={`font-mono text-[10px] ${c.faint}`}>{compactDateNumeric(latestBrowser.createdAt)}</span>}
          <ReloadButton onClick={load} loading={state === 'loading'} theme={theme} />
        </div>
      }
    >
      {lightbox && <ScreenshotLightbox src={lightbox.src} label={lightbox.label} description={lightbox.description} onClose={() => setLightbox(null)} />}
      {historyLabel && (
        <div className="mb-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
          Latest visible · {timelineDateLabel(new Date(historyLabel))}
        </div>
      )}
      {tiles}
      {gallery}
    </Widget>
  )
}
